import { npubEncode } from '@nostr/tools/nip19'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { mutePrivately, mutePublicly, unmute } from '@/atoms/mutes'
import { canWriteAtom, unwrappedSignerAtom, currentPubkeyAtom } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  BellIcon,
  BellOffIcon,
  Copy01Icon,
  EllipsisIcon,
  Tag01Icon,
  Tag02Icon,
  TicketStarIcon
} from '@hugeicons/core-free-icons'
import {
  unwrappedHasPremiumRelaysAtom,
  unwrappedPremiumSubscribersAtom,
  addPremiumSubscriber
} from '@/atoms/premium'
import { useAtomValue } from 'jotai'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SaveToFollowSetItems from './SaveToFollowSetItems'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { setPetname, unwrappedFollowListAtom, unwrappedPetnamesAtom } from '@/atoms/lists'

export default function ProfileOptions({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation()
  const signer = useAtomValue(unwrappedSignerAtom) ?? null
  const canWrite = useAtomValue(canWriteAtom)
  const accountPubkey = useAtomValue(currentPubkeyAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const isMuted = useMemo(() => mutePubkeySet.has(pubkey), [mutePubkeySet, pubkey])
  const hasPremiumRelays = useAtomValue(unwrappedHasPremiumRelaysAtom)
  const premiumSubscribers = useAtomValue(unwrappedPremiumSubscribersAtom)
  const isPremiumSubscriber = useMemo(
    () => premiumSubscribers?.has(pubkey) ?? false,
    [premiumSubscribers, pubkey]
  )
  const followList = useAtomValue(unwrappedFollowListAtom)
  const petnames = useAtomValue(unwrappedPetnamesAtom)
  const [petnameDialogOpen, setPetnameDialogOpen] = useState(false)
  const [petname, setPetnameValue] = useState('')
  const isFollowing = followList?.includes(pubkey) ?? false

  if (pubkey === accountPubkey) return null

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="icon" className="rounded-full">
            <HugeiconsIcon icon={EllipsisIcon} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(npubEncode(pubkey))}>
            <HugeiconsIcon icon={Copy01Icon} />
            {t('Copy user ID')}
          </DropdownMenuItem>
          {canWrite &&
            (isMuted ? (
              <DropdownMenuItem
                onClick={() => unmute(pubkey)}
                className="text-destructive focus:text-destructive"
              >
                <HugeiconsIcon icon={BellIcon} />
                {t('Unmute user')}
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    if (signer?.nip04Decrypt) mutePrivately(pubkey)
                  }}
                  className="text-destructive focus:text-destructive"
                  disabled={!signer?.nip04Decrypt}
                  title={
                    !signer?.nip04Decrypt
                      ? t('Your login method does not support encryption')
                      : undefined
                  }
                >
                  <HugeiconsIcon icon={BellOffIcon} />
                  {t('Mute user privately')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => mutePublicly(pubkey)}
                  className="text-destructive focus:text-destructive"
                >
                  <HugeiconsIcon icon={BellOffIcon} />
                  {t('Mute user publicly')}
                </DropdownMenuItem>
              </>
            ))}
          {canWrite && hasPremiumRelays && !isPremiumSubscriber && (
            <DropdownMenuItem onClick={() => addPremiumSubscriber(pubkey)}>
              <HugeiconsIcon icon={TicketStarIcon} />
              {t('Add to premium subscribers')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <SaveToFollowSetItems pubkey={pubkey} />
          {canWrite && isFollowing && (
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault()
                setPetnameValue(petnames?.get?.(pubkey) ?? '')
                setPetnameDialogOpen(true)
              }}
            >
              <HugeiconsIcon icon={petnames?.get?.(pubkey) ? Tag02Icon : Tag01Icon} />
              {t('Edit petname')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={petnameDialogOpen} onOpenChange={setPetnameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Edit petname')}</DialogTitle>
          </DialogHeader>
          <Label htmlFor="petname">{t('Petname')}</Label>
          <Input
            id="petname"
            value={petname}
            onChange={(event) => setPetnameValue(event.target.value)}
            autoFocus
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPetnameDialogOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={async () => {
                await setPetname(pubkey, petname.trim())
                setPetnameDialogOpen(false)
              }}
            >
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
